import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin } from 'rxjs';
import { take } from 'rxjs/operators';

import { getGeneralErrorMessage } from 'src/app/core/interceptors/generic-error-handling-interceptors';
import { initialState } from 'src/app/domains/auth/store';

import { Company, KazooInstance, KazooMetadata } from 'src/app/domains/companies/models';
import { CompanyService } from 'src/app/domains/companies/services';
import { PbxService } from 'src/app/domains/companies/services/pbx.service';
import { Pbx } from 'src/app/domains/companies/models/pbx.model';
import { Queue, QueueUser } from 'src/app/domains/queues/models/queues.model';
import { QueuesService } from 'src/app/domains/queues/services/queues.service';
import { User } from 'src/app/domains/users/models';
import { UsersService } from 'src/app/domains/users/services';
import { NotificationService } from 'src/app/shared/services/notification/notification.service';
import { QueueModulePopupComponent } from '../queue-module-popup/queue-module-popup.component';
import { QueueRingStrategyPopupComponent } from '../queue-ring-strategy-popup/queue-ring-strategy-popup.component';


@Component({
  selector: 'gdp-company-queues',
  templateUrl: './company-queues.component.html',
  styleUrls: ['./company-queues.component.scss']
})
export class CompanyQueuesComponent implements OnInit {
  @Input() company: Company;
  public queues: Queue[] = [];
  public users: User[] = [];
  public loading = {
    queues: false,
    queueModule: false
  }
  public restartLoading: boolean = false;
  public logoutUsersLoading: boolean = false;
  public loadingRecreate: boolean = false;
  public kazooMetadata: KazooMetadata;
  public queueModules: string[];
  private pbx: Pbx;

  constructor(
    private queuesService: QueuesService,
    private pbxService: PbxService,
    private usersService: UsersService,
    private notification: NotificationService,
    private translate: TranslateService,
    private modalService: NgbModal
  ) { }

  ngOnInit() {
    this.getQueues();
    this.getQueueModules();
  }

  getQueueModules() {
    this.loading.queueModule = true;
    forkJoin({
      kazooMetadata: this.pbxService.getKazooMetadata(),
      pbx: this.pbxService.getPbxes(this.company.pbxId)
    }).subscribe(
      (response:{ kazooMetadata: KazooMetadata, pbx: Pbx })=> {
        this.loading.queueModule = false;
        this.kazooMetadata = response.kazooMetadata;
        this.pbx = response.pbx;
        this.queueModules = this.findKazooInstance().available_queue_modules;
        this.loading.queueModule = false;
      },
      () => { this.loading.queueModule = false }
    );
  }

  get queueModule() {
    // find instance that is used in PBX
    // otherwise use default_kazoo_instance_id
    const kazooInstance = this.findKazooInstance();
    return kazooInstance.available_queue_modules.includes(this.pbx.queue_module)
      ? this.pbx.queue_module
      : kazooInstance.default_queue_module;
  }

  private findKazooInstance() {
    return this.kazooMetadata?.kazoo_instances.find(
      i => i.id === this.pbx.kazoo_instance_id
    ) || this.kazooMetadata.kazoo_instances.find(
      i => i.id === this.kazooMetadata.default_kazoo_instance_id
    );
  }

  selectQueueModule(m:string) {
    if (m !== this.pbx.queue_module) {
      this.loading.queueModule = true;
      this.pbxService.updatePbx({
        ...this.pbx,
        queue_module: m
      }).subscribe(
        (r: Pbx)=> {
          this.pbx = r;
          this.notification.success('company.queues.notifications.queue-default-module-success');
          this.loading.queueModule = false;
        },()=> {
          this.loading.queueModule = false;
        }
      );
    }
  }

  get someSelected() {
    return this.queues.length !== 0 &&  this.queues?.some(q => q._selected === true);
  }

  get everySelected() {
    return this.queues.length !== 0 && this.queues?.every(q => q._selected === true);
  }

  toggleSelectAll() {
    const selected = this.everySelected;
    this.queues.forEach((q:Queue) => {
      q._selected = !selected;
    });
  }

  toggleSelect(q:Queue) {
    q._selected = !q._selected;
  }

  getQueues() {
    this.loading.queues = true;
    forkJoin({
      queues: this.queuesService.getQueues(this.company.pbxId),
      users: this.usersService.getUsers(this.company.companyId)
    })
      .subscribe(
        (res: { queues: Queue[], users: User[] }
        ) => {
          // mark all not selected
          this.queues = res.queues.map(q => ({ ...q, _selected: false }) );
          this.users = res.users;
          this.loading.queues = false;
        }, () => {
          this.queues = [];
          this.loading.queues = false;
        });
  }

  getUserQueue(id: string) {
    const user = this.users.find(u => u.pbxId == id);
    return user ? `${user.firstName} ${user.lastName} <span class="vox-chip vox-chip_royal-blue-text ml-1">${user.extension}</span>` : '-';
  }

  restartQueues() {
    this.restartLoading = true;
    this.queuesService.restartQueues(this.company.pbxId).subscribe((res: any) => {
      this.notification.success('company.queues.notifications.restart-success');
      this.restartLoading = false;
    }, () => {
      this.restartLoading = false;
    });
  }

  logoutUsersQueue() {
    this.logoutUsersLoading = true;
    this.queuesService.logoutUsersQueue(this.company.pbxId).subscribe((res: any) => {
      this.notification.success('company.queues.notifications.logout-users-success');
      this.logoutUsersLoading = false;
    }, () => {
      this.logoutUsersLoading = false;
    });
  }

  logoutUserQueue(user: QueueUser) {
    user._logout = true;
    this.queuesService.logoutUserQueue(this.company.pbxId, user.id).subscribe((res: any) => {
      this.notification.success(
        this.translate.instant(
          'company.queues.notifications.logout-user-success',
          {
            queueUser: `<b>${user.first_name} ${user.last_name} (${user.extension_number})</b>`
          }
        )
      );
      user._logout = false;
    }, () => {
      user._logout = false;
    });
  }

  loginUserQueue(user: QueueUser) {
    user._login = true;
    this.queuesService.loginUserQueue(this.company.pbxId, user.id).subscribe((res: any) => {
      this.notification.success(
        this.translate.instant(
          'company.queues.notifications.login-user-success',
          {
            queueUser: `<b>${user.first_name} ${user.last_name} (${user.extension_number})</b>`
          }
        )
      );
      user._login = false;
    }, () => {
      user._login = false;
    });
  }

  resolveConflictsQueue(queue: Queue) {
    queue._resolveConflicts = true;
    this.queuesService.resolveConflictsQueue(this.company.pbxId, queue.id).subscribe((res: any) => {
      this.notification.success(
        this.translate.instant(
          'company.queues.notifications.resolve-conflicts-queue-success',
          {
            queue: `<b>${queue.name}</b>`
          }
        )
      );
      queue._resolveConflicts = false;
    }, () => {
      queue._resolveConflicts = false;
    });
  }

  public changeQueueModule(queue?: Queue) {
    const queues = queue ? [{...queue}] : [...this.queues.filter(q => q._selected === true)];
    const kazoo = this.findKazooInstance();
    const modal = this.modalService.open(QueueModulePopupComponent);
    modal.componentInstance.pbx = { ...this.pbx };
    modal.componentInstance.queues = queues;
    modal.componentInstance.queueModules =
      [kazoo.default_queue_module]
        .concat(
          kazoo.available_queue_modules.filter(m => m !== kazoo.default_queue_module)
        );
    modal.componentInstance.moduleChanged.pipe(take(1))
      .subscribe(
        (module: string)=> {
          this.queues.forEach(q => {
            if (q._selected) {
              q.module = module;
            }
          });
          this.notification.success(
            this.translate.instant(
              'company.queues.notifications.queue-module-change-success',
              { name: queues.map(q => q.name).join(', '), module: module }
            )
          );
          this.getQueues();
        }
      );
  }

  public onAutomaticRecreateToggle(value: boolean, queue: Queue) {
    const old_value = queue.is_recreatable;
    queue.is_recreatable = value;
    queue._automaticRecreating = true;
    this.queuesService.updateQueue(this.company.pbxId, queue)
      .subscribe(
        (result: Queue) => {
          if(result.is_recreatable) {
            this.notification.success(
              this.translate.instant(
                'company.queues.notifications.automatic-recreation-enable-success',
                { queue: queue.name }
              )
            );
          } else {
            this.notification.success(
              this.translate.instant(
                'company.queues.notifications.automatic-recreation-disable-success',
                { queue: queue.name }
              )
            );
          }
          queue._automaticRecreating = false;
        }, () => {
          queue.is_recreatable = old_value;
          queue._automaticRecreating = false;
        }
      );
  }

  public recreateQueue(queue: Queue) {
    queue._recreating = true;
    this.queuesService.recreateQueue(this.company.pbxId, queue.id).subscribe(
      () => {
        this.notification.success(
          this.translate.instant(
            'company.queues.notifications.recreate-queue-success',
            { queue: `<b>${queue.name}</b>` }
          )
        );
        queue._recreating = false;
      }, (httpError: HttpErrorResponse) => {
        let errorMsg: string;
        const failed_on = httpError.error.body?.failed_on;
        if(failed_on){
          if (['resource_creation', 'resource_initialization', 'old_resource_deletion'].includes(failed_on)) {
            errorMsg =`company.queues.notifications.failures.${failed_on}`;
          } else {
            errorMsg = getGeneralErrorMessage(httpError);
          }
        } else {
          errorMsg = getGeneralErrorMessage(httpError);
        }
        this.notification.error(errorMsg);
        queue._recreating = false;
      });
  }

  public selectRingStrategy(queue: Queue): void {
    const modal = this.modalService.open(QueueRingStrategyPopupComponent);
    modal.componentInstance.queue = queue;
    modal.componentInstance.pbx = { ...this.pbx };
    modal.componentInstance.emitRingStrategy.subscribe((res: Queue) => {
      this.notification.success('company.queues.ring-strategy-popup.success');
      this.getQueues();
    });
  }
}
